import { useCallback, useEffect, useRef, useState } from "react";
import { resolveAssetPath } from "../../shared/utilities/assets";
import type { WeddingData } from "../../types/wedding";

interface Props {
  music: WeddingData["music"];
  copy: Record<string, string>;
  playSignal: number;
}

export function MusicPlayer({ music, copy, playSignal }: Props) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const fadeTimer = useRef<number | null>(null);
  const [playing, setPlaying] = useState(false);

  const stopFade = useCallback(() => {
    if (fadeTimer.current !== null) window.clearInterval(fadeTimer.current);
    fadeTimer.current = null;
  }, []);

  const pause = useCallback(() => {
    stopFade();
    audioRef.current?.pause();
    setPlaying(false);
  }, [stopFade]);

  const play = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio || !music.enabled || !music.url) return;
    stopFade();
    audio.volume = 0;
    try {
      await audio.play();
      setPlaying(true);
      fadeTimer.current = window.setInterval(() => {
        audio.volume = Math.min(music.volume, audio.volume + 0.05);
        if (audio.volume >= music.volume) stopFade();
      }, 90);
    } catch {
      setPlaying(false);
    }
  }, [music.enabled, music.url, music.volume, stopFade]);

  useEffect(() => {
    if (playSignal > 0 && music.autoplayAfterInteraction) void play();
    return stopFade;
  }, [music.autoplayAfterInteraction, play, playSignal, stopFade]);

  if (!music.enabled || !music.url) return null;

  return (
    <>
      <button className="icon-btn" type="button" onClick={() => (playing ? pause() : void play())}>
        {playing ? copy.musicOn : copy.musicOff}
      </button>
      <audio
        ref={audioRef}
        loop={music.loop}
        preload="none"
        src={resolveAssetPath(music.url)}
        onPause={() => setPlaying(false)}
        onPlay={() => setPlaying(true)}
      />
    </>
  );
}
